import { getPartQueryOptions } from "@packages/api-client/hooks/equipmentController";
import type { UpdatePartMutationRequest } from "@packages/api-client/types/equipmentController";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useUpdatePart } from "@/api/equipment";
import { PageHeader } from "@/components/PageHeader";
import { FormSkeleton } from "@/components/ui/skeleton";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { PartForm } from "../-components/PartForm";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/equipment/parts/$partId/edit",
)({
	// Roadmap rule 2: edits never queue offline (conflict-prone, low urgency).
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	component: EditPartPage,
});

function EditPartPage() {
	const { orgId, partId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();

	const { data: part, isLoading } = useQuery(
		getPartQueryOptions(orgId, partId),
	);

	const updateMutation = useUpdatePart();

	const handleSubmit = (formData: UpdatePartMutationRequest) =>
		updateMutation
			.mutateAsync({ organizationId: orgId, partId, data: formData })
			.then(() => {
				navigate({
					to: "/app/org/$orgId/equipment/parts/$partId",
					params: { orgId, partId },
				});
			});

	if (isLoading) {
		return <FormSkeleton />;
	}

	if (!part) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-base-content/70">{t("equipment.parts.notFound")}</p>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-6 h-full">
			<PageHeader title={t("equipment.parts.edit.title")} showBackButton />
			<PartForm
				part={part}
				orgId={orgId}
				onSubmit={handleSubmit}
				isSubmitting={updateMutation.isPending}
				onCancel={() =>
					navigate({
						to: "/app/org/$orgId/equipment/parts/$partId",
						params: { orgId, partId },
					})
				}
			/>
		</div>
	);
}
